import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://novaaiwebsites.com';

const smsSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/sms-optin#webpage`,
  url: `${siteUrl}/sms-optin`,
  name: 'SMS Opt-In | NOVA AI DIGITAL — Exclusive Offers & Updates',
  description:
    'Sign up to receive exclusive SMS updates, promotions, and project news from NOVA AI DIGITAL. No spam, unsubscribe anytime.',
  isPartOf: { '@id': `${siteUrl}/#website` },
  dateModified: '2026-03-17',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'SMS Opt-In', item: `${siteUrl}/sms-optin` },
    ],
  },
};

export default function SmsOptIn() {
  const [pageLoaded, setPageLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setPageLoaded(true), 100);

    // Ensure this page is indexable
    let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.content = 'index, follow';

    // Inject canonical for this page
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${siteUrl}/sms-optin`;

    // Inject structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page', 'sms-optin');
    script.textContent = JSON.stringify(smsSchema);
    document.head.appendChild(script);

    return () => {
      clearTimeout(t);
      document.querySelectorAll('script[data-page="sms-optin"]').forEach((el) => el.remove());
    };
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!agreed) return;
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => urlEncoded.append(key, value.toString()));

    try {
      const res = await fetch('https://readdy.ai/api/form/d6gt5rbi06gv2c9ufldg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        form.reset();
        setCharCount(0);
        setAgreed(false);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 6000);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white font-sans">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/74809ee30fca19c1acffe2fc12cb4256/a52f883efcaf337c8d3180c86ddb8128.png"
              alt="NOVA AI DIGITAL Logo"
              className="h-12 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </div>
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="relative pt-24 pb-16 overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-brand-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-2xl mx-auto px-4 md:px-6 text-center relative z-10">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500/15 border border-brand-500/30 rounded-full text-brand-400 text-sm font-medium mb-6"
            style={{
              opacity: pageLoaded ? 1 : 0,
              transform: pageLoaded ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.7s cubic-bezier(0.16,1,0.3,1) 0.1s',
            }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-message-3-line" />
            </div>
            SMS Updates
          </div>

          <h1
            className="text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-5"
            style={{
              opacity: pageLoaded ? 1 : 0,
              transform: pageLoaded ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 0.2s',
            }}
          >
            Stay in the Loop with Exclusive Offers
          </h1>

          <p
            className="text-zinc-400 text-base md:text-lg leading-relaxed mb-4"
            style={{
              opacity: pageLoaded ? 1 : 0,
              transform: pageLoaded ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 0.35s',
            }}
          >
            Sign up to receive text message updates from NOVA AI DIGITAL — including
            special promotions, tips, and project updates. No spam, ever.
          </p>

          {/* Perks */}
          <div
            className="flex flex-wrap justify-center gap-4 mb-10"
            style={{
              opacity: pageLoaded ? 1 : 0,
              transform: pageLoaded ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 0.45s',
            }}
          >
            {[
              { icon: 'ri-price-tag-3-line', label: 'Exclusive Discounts' },
              { icon: 'ri-notification-3-line', label: 'Project Updates' },
              { icon: 'ri-shield-check-line', label: 'No Spam, Ever' },
            ].map((perk) => (
              <div
                key={perk.label}
                className="flex items-center gap-2 px-4 py-2 bg-zinc-800/60 border border-zinc-700/50 rounded-full text-sm text-zinc-300"
              >
                <div className="w-4 h-4 flex items-center justify-center text-brand-400">
                  <i className={perk.icon} />
                </div>
                {perk.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="max-w-xl mx-auto px-4 md:px-6 pb-24">
        <div
          className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 md:p-10 shadow-2xl"
          style={{
            opacity: pageLoaded ? 1 : 0,
            transform: pageLoaded ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.9s cubic-bezier(0.16,1,0.3,1) 0.5s',
          }}
        >
          {submitStatus === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center bg-green-500/15 border border-green-500/30 rounded-full mx-auto mb-5">
                <i className="ri-check-line text-3xl text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">You&apos;re In!</h2>
              <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                Thanks for signing up! You&apos;ll start receiving exclusive SMS updates from NOVA AI DIGITAL soon.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-zinc-900 font-semibold rounded-lg hover:bg-zinc-100 transition-all cursor-pointer whitespace-nowrap text-sm"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-line" />
                </div>
                Back to Home
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-white mb-1">Sign Up for SMS Updates</h2>
              <p className="text-zinc-500 text-sm mb-7">Fill out the form below to opt in.</p>

              <form
                data-readdy-form
                id="sms-optin-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1.5">
                    Full Name <span className="text-brand-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="John Smith"
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 text-sm focus:outline-none focus:border-brand-500 transition-colors"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1.5">
                    Mobile Phone Number <span className="text-brand-400">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="(555) 000-0000"
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 text-sm focus:outline-none focus:border-brand-500 transition-colors"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 text-sm focus:outline-none focus:border-brand-500 transition-colors"
                  />
                </div>

                {/* Note */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1.5">
                    Anything you&apos;d like us to know? <span className="text-zinc-500">(optional)</span>
                  </label>
                  <textarea
                    name="note"
                    rows={3}
                    maxLength={500}
                    placeholder="e.g. I&apos;m interested in a new website for my business..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 text-sm focus:outline-none focus:border-brand-500 transition-colors resize-none"
                  />
                  <p className="text-right text-xs text-zinc-600 mt-1">{charCount}/500</p>
                </div>

                {/* Consent */}
                <div className="flex items-start gap-3 p-4 bg-zinc-800/50 border border-zinc-700/50 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setAgreed(!agreed)}
                    className={`mt-0.5 w-5 h-5 flex-shrink-0 flex items-center justify-center rounded border transition-all cursor-pointer ${
                      agreed
                        ? 'bg-brand-500 border-brand-500'
                        : 'bg-transparent border-zinc-600 hover:border-zinc-400'
                    }`}
                    aria-label="Agree to SMS terms"
                  >
                    {agreed && <i className="ri-check-line text-xs text-white" />}
                  </button>
                  <p className="text-zinc-400 text-xs leading-relaxed">
                    By checking this box, I agree to receive recurring automated marketing text messages
                    from <strong className="text-zinc-200">NOVA AI DIGITAL</strong> at the phone number provided.
                    Message &amp; data rates may apply. Reply <strong className="text-zinc-200">STOP</strong> to
                    unsubscribe at any time. View our{' '}
                    <Link to="/" className="text-brand-400 hover:underline cursor-pointer">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </div>

                {submitStatus === 'error' && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-error-warning-line" />
                    </div>
                    Something went wrong. Please try again.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || !agreed}
                  className="w-full py-3.5 bg-white text-zinc-900 font-bold rounded-lg hover:bg-zinc-100 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin" />
                      </div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-message-3-line" />
                      </div>
                      Opt In for SMS Updates
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Trust note */}
        <p
          className="text-center text-zinc-600 text-xs mt-6 leading-relaxed"
          style={{
            opacity: pageLoaded ? 1 : 0,
            transition: 'opacity 0.8s ease 0.9s',
          }}
        >
          Your information is kept private and never sold. You can opt out at any time by replying STOP.
        </p>
      </div>

      {/* Footer strip */}
      <div className="border-t border-zinc-800 py-6">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-zinc-600 text-xs">&copy; 2025 NOVA AI DIGITAL — Veteran Owned &amp; Operated</p>
          <button
            onClick={scrollToTop}
            className="flex items-center gap-1.5 text-zinc-500 hover:text-white transition-colors text-xs cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-up-line" />
            </div>
            Back to top
          </button>
        </div>
      </div>
    </div>
  );
}
